/**
 * Given a graphql object shape with __typename declared
 * filters out types based on __typename keys.
 * @example
 * type Example = IsTypename<'UserProfile', { __typename: 'UserProfile'; foo: string} | { __typename: '%other' }>;
 * // => type Example = { __typename: 'UserProfile'; foo: string };
 */
type IsTypename<
  T extends { __typename: string },
  K extends T['__typename'],
> = T extends { __typename: K } ? T : never;

/**
 * Given a `typename` string, creates a function that that can be used
 * to filter Graphql objects with `__typename` down to specific key.
 *
 * WARNING! -- The argument is not checked in any way.
 * Use: `filterByTypename()` to be type safe!
 * @example
 * const isUserProfile = isTypename('UserProfile');
 *
 * const originalItems = [
 *   { __typename: 'UserProfile', foo: 'foo' },
 *   { __typename: '%other' }
 * ];
 *
 * type ItemsWeWant = { __typename: 'UserProfile', foo: string };
 *
 * const itemsWeWant: ItemsWeWant[] = originalItems
 *   .filter(isUserProfile);
 */
const isTypename =
  <K extends string>(typename: K) =>
  <T extends { __typename: string }>(item: T): item is IsTypename<T, K> => {
    return item.__typename === typename;
  };

/**
 * Given a array of items with `__typename`s, will filter items down
 * to a specific `__typename`.
 *
 * IMPORTANT: Be sure that `__typename`s are string literals and not typed as `string`,
 * or you'll get a `never[]` back.
 * @example
 * const originalItems = [
 *   { __typename: 'UserProfile' as const, foo: 'foo' },
 *   { __typename: '%other' as const }
 * ];
 *
 * type ItemsWeWant = { __typename: 'UserProfile', foo: string };
 *
 * const itemsWeWant: ItemsWeWant[] = filterByTypename(originalItems, 'UserProfile');
 */
export const filterByTypename = <
  T extends { __typename: string },
  K extends T['__typename'],
>(
  items: readonly T[],
  typename: K,
): Array<IsTypename<T, K>> => {
  return items.filter(isTypename(typename));
};
